// Utility functions for staging and applying MuJoCo poses
// All functions here are pure (return values) except applyPoseToObject
// to make debugging and testing easier.

import * as THREE from "three";
import { usePhysicsStore } from "../stores/physicsStore";
import { logger } from "../utils/logger";

// Converts a MuJoCo world-space pose to a local pose for a given parent.
// worldPose: [px, py, pz, qw, qx, qy, qz]
// robotGroup: optional THREE.Object3D used to convert MuJoCo Z-up world into viewer world
// parent: THREE.Object3D that will own the target object
export function convertPoseToLocal(worldPose, robotGroup, parent) {
  const [px, py, pz, qw, qx, qy, qz] = worldPose || [0, 0, 0, 1, 0, 0, 0];

  // Compose MuJoCo world matrix (Z-up), with unit scale to avoid propagating any non-uniform scale
  const worldPos = new THREE.Vector3(px, py, pz);
  const worldQuat = new THREE.Quaternion(qx, qy, qz, qw).normalize();
  const unit = new THREE.Vector3(1, 1, 1);
  const worldMat = new THREE.Matrix4().compose(worldPos, worldQuat, unit);

  // Optionally rotate into viewer world using robotGroup's world rotation only
  try {
    const disableByStore = usePhysicsStore.getState().poseNoGroupConversion === true;
    const disableByWindow =
      typeof window !== "undefined" && window.__rolloutBypassGroupConversion === true;
    const disableConv = disableByStore || disableByWindow;
    if (!disableConv && robotGroup && typeof robotGroup.getWorldQuaternion === "function") {
      const groupWorldQ = new THREE.Quaternion();
      robotGroup.getWorldQuaternion(groupWorldQ);
      const rotOnly = new THREE.Matrix4().makeRotationFromQuaternion(groupWorldQ);
      worldMat.premultiply(rotOnly);
    }
  } catch (error) {
    logger.warn("[poseUtils] Failed to apply robot group rotation to world pose", error);
  }

  // Compute local matrix relative to parent: local = parentWorld^-1 * world
  // Use a parent transform with unit scale to avoid inheriting non-uniform scaling.
  let localMat = worldMat.clone();
  if (parent) {
    try {
      if (typeof parent.updateWorldMatrix === "function") parent.updateWorldMatrix(true, false);
      const parentPos = new THREE.Vector3();
      const parentQuat = new THREE.Quaternion();
      const parentScale = new THREE.Vector3();
      parent.matrixWorld.decompose(parentPos, parentQuat, parentScale);
      const parentWorldNoScale = new THREE.Matrix4().compose(
        parentPos,
        parentQuat,
        new THREE.Vector3(1, 1, 1)
      );
      const parentInv = parentWorldNoScale.invert();
      localMat = parentInv.multiply(worldMat);
    } catch (error) {
      logger.warn("[poseUtils] Failed to convert world pose to local space", error);
    }
  }

  // Decompose local matrix (ignore any scale, keep transforms clean)
  const localPos = new THREE.Vector3();
  const localQuat = new THREE.Quaternion();
  const localScale = new THREE.Vector3();
  localMat.decompose(localPos, localQuat, localScale);

  return {
    position: localPos,
    quaternion: localQuat,
    debug: {
      worldPos,
      worldQuat,
      localScale,
    },
  };
}

// Applies a local pose result from convertPoseToLocal to an object
export function applyPoseToObject(object3D, localPose) {
  if (!object3D || !localPose) return;
  object3D.position.copy(localPose.position);
  object3D.quaternion.copy(localPose.quaternion);
}
